import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { historyService, ConversationHistoryItem } from '../../services/historyService';
import { formatDistanceToNow } from 'date-fns';

interface ChatMenuProps {
  onSelectConversation?: (conversation: ConversationHistoryItem) => void;
  onNewChat?: () => void;
  currentSessionId?: string;
  refreshKey?: number;
}

const ChatMenu: React.FC<ChatMenuProps> = ({ onSelectConversation, onNewChat, currentSessionId, refreshKey }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [chatExpanded] = useState(true); // Chat section always expanded (not togglable)
  const [historyExpanded, setHistoryExpanded] = useState(false); // Chat History collapsed by default
  const [conversations, setConversations] = useState<ConversationHistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { userName, logout } = useAuth();

  useEffect(() => {
    // Load history when component mounts
    if (location.pathname === '/chat') {
      loadConversationHistory();
    }
  }, [location.pathname]);

  // Reload history when refreshKey changes (triggered by new messages)
  useEffect(() => {
    if (refreshKey && refreshKey > 0) {
      loadConversationHistory();
      // Auto-expand history section on first message to show the new conversation
      if (!historyExpanded) {
        setHistoryExpanded(true);
      }
    }
  }, [refreshKey, historyExpanded]);

  const loadConversationHistory = async (forceRefresh = false) => {
    setLoadingHistory(true);
    try {
      const history = await historyService.loadConversationHistory(forceRefresh);
      setConversations(history);
    } catch (error) {
      console.error('Failed to load history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleSelectConversation = (conversation: ConversationHistoryItem) => {
    if (onSelectConversation) {
      // If we're on the chat page, use the callback directly
      onSelectConversation(conversation);
    } else {
      // If we're on another page, navigate to chat and store the conversation for loading
      sessionStorage.setItem('selectedConversation', JSON.stringify(conversation));
      navigate('/chat');
    }
    // Close mobile menu after selection
    if (window.innerWidth < 768) {
      setIsOpen(false);
    }
  };

  const formatTimeAgo = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return 'Recently';
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNewChatClick = () => {
    // Close mobile menu if applicable
    if (window.innerWidth < 768) {
      setIsOpen(false);
    }

    // Set a flag to indicate we want to start a new chat
    sessionStorage.setItem('startNewChat', 'true');

    // If we're on the Chat page and have the callback, use it for instant response
    if (location.pathname === '/chat' && onNewChat) {
      onNewChat();
    } else {
      // Otherwise, navigate to chat page which will pick up the flag
      navigate('/chat');
    }
  };

  const menuItems = [
    {
      label: 'Chat',
      path: '/chat',
      expandable: true,
      alwaysExpanded: true, // Chat is always expanded
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      ),
      onClick: () => {
        navigate('/chat');
      },
    },
    {
      label: 'Chat History',
      path: null,
      expandable: true,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      onClick: () => {
        setHistoryExpanded(!historyExpanded);
        if (!historyExpanded && conversations.length === 0) {
          loadConversationHistory();
        }
      },
    },
    {
      label: 'Job Tracking',
      path: '/jobtracking',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
      ),
      onClick: () => {
        navigate('/jobtracking');
      },
    },
    {
      label: 'Profile',
      path: '/profile',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      ),
      onClick: () => {
        navigate('/profile');
      },
    },
    {
      label: 'Settings',
      path: '/settings',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      onClick: () => {
        navigate('/settings');
      },
    },
  ];

  const logoutButton = {
    label: 'Logout',
    path: null,
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
      </svg>
    ),
    onClick: handleLogout,
  };

  return (
    <>
      {/* Mobile menu button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="md:hidden fixed top-4 left-4 z-40 p-2 bg-white rounded-jobesta shadow-jobesta-card border border-jobesta-gray-40"
      >
        <svg
          className="w-6 h-6 text-jobesta-gray-90"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d={isOpen ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16M4 18h16'}
          />
        </svg>
      </button>

      {/* Sidebar */}
      <div
        className={`${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } md:translate-x-0 fixed md:relative w-64 h-full bg-white border-r border-jobesta-gray-40 transition-transform duration-300 ease-in-out z-50 flex flex-col`}
      >
        <div className="flex-1 flex flex-col p-4 min-h-0">
          <div className="flex-shrink-0">
            <h1 className="text-2xl mb-8 tracking-tight">
              <span className="font-black text-jobesta-blue">Job</span>
              <span className="font-extralight text-jobesta-blue">esta</span>
            </h1>
            
            {/* User info */}
            <div className="mb-6 p-3 bg-jobesta-gray-30 rounded-jobesta">
              <p className="text-sm text-jobesta-gray-60">Logged in as</p>
              <p className="font-medium truncate text-jobesta-gray-90">{userName || 'User'}</p>
            </div>

          </div>

          {/* Menu items */}
          <nav className="flex-1 space-y-2 overflow-y-auto min-h-0">
            {menuItems.map((item, index) => {
              const isActive = item.path && location.pathname === item.path;
              const isChatItem = item.label === 'Chat';
              const isHistoryItem = item.label === 'Chat History';
              const isExpanded = isChatItem ? chatExpanded : (isHistoryItem ? historyExpanded : false);
              const isAlwaysExpanded = item.alwaysExpanded;

              return (
                <div key={index}>
                  <button
                    onClick={item.onClick}
                    data-onboarding-target={
                      item.label === 'Profile' ? 'profile-menu' :
                      item.label === 'Chat' ? 'chat-menu' :
                      item.label === 'Job Tracking' ? 'jobtracking-menu' :
                      undefined
                    }
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-jobesta transition-colors ${
                      isActive
                        ? 'bg-jobesta-blue text-white hover:bg-jobesta-darkblue'
                        : 'text-jobesta-gray-70 hover:bg-jobesta-gray-30 hover:text-jobesta-blue'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <span className={isActive ? 'text-white' : ''}>{item.icon}</span>
                      <span className="font-medium">{item.label}</span>
                    </div>
                    {(isChatItem || isHistoryItem) && !isAlwaysExpanded && (
                      <svg
                        className={`w-4 h-4 transition-transform ${
                          isExpanded ? 'rotate-90' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    )}
                  </button>

                  {/* Chat Expanded Content: New Chat Button */}
                  {isChatItem && chatExpanded && (
                    <div className="mt-2 ml-6 mr-2 space-y-1">
                      {/* New Chat Button */}
                      <button
                        onClick={handleNewChatClick}
                        className="w-full flex items-center space-x-2 px-3 py-2 rounded-jobesta transition-colors text-jobesta-gray-70 hover:bg-jobesta-gray-30 hover:text-jobesta-blue"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        <span className="text-sm font-medium">New Chat</span>
                      </button>
                    </div>
                  )}

                  {/* Chat History Expanded Content */}
                  {isHistoryItem && historyExpanded && (
                    <div className="mt-2 ml-6 mr-2 space-y-1 max-h-64 overflow-y-auto border-l border-jobesta-gray-40 pl-2">
                      {loadingHistory ? (
                        <div className="py-2 px-2 text-sm text-jobesta-gray-60">
                          Loading history...
                        </div>
                      ) : conversations.length === 0 ? (
                        <div className="py-2 px-2 text-sm text-jobesta-gray-60">
                          No conversations yet
                        </div>
                      ) : (
                        conversations.map((conv) => (
                          <button
                            key={conv.session_id}
                            onClick={() => handleSelectConversation(conv)}
                            className={`w-full text-left px-2 py-2 rounded text-sm transition-colors ${
                              currentSessionId === conv.session_id
                                ? 'bg-jobesta-blue bg-opacity-10 text-jobesta-blue'
                                : 'text-jobesta-gray-70 hover:bg-jobesta-gray-30'
                            }`}
                          >
                            <div className="truncate text-xs leading-tight">
                              {conv.first_message}
                            </div>
                            <div className="text-xs text-jobesta-gray-50 mt-1">
                              {formatTimeAgo(conv.updated_at)}
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Logout button at bottom */}
          <div className="flex-shrink-0 pt-4 border-t border-jobesta-gray-40">
            <button
              onClick={logoutButton.onClick}
              className="w-full flex items-center space-x-3 px-3 py-2 rounded-jobesta text-jobesta-red hover:bg-jobesta-red hover:bg-opacity-10 transition-colors"
            >
              <span>{logoutButton.icon}</span>
              <span className="font-medium">{logoutButton.label}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile overlay */}
      {isOpen && (
        <div
          onClick={() => setIsOpen(false)}
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
        />
      )}
    </>
  );
};

export default ChatMenu;